import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Phone, Mail, MapPin } from 'lucide-react';

// TikTok Icon Component
const TikTokIcon = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
  </svg>
);

// YouTube Icon Component
const YouTubeIcon = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
  </svg>
);

export const Contact = () => {
  return (
    <>
      <Helmet>
        <title>Contact | Wild Ones LLC</title>
        <meta
          name="description"
          content="Get in touch with Wild Ones LLC. Questions about our southern-style clothing? Call us at 803-272-5881 or connect with us on social media."
        />
        <link rel="canonical" href="https://wildones.com/contact" />
      </Helmet>

      <main className="pt-[116px]" data-testid="contact-page">
        {/* Page Header */}
        <section className="bg-wild-khaki/30 py-16 md:py-24">
          <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
            <h1
              className="font-headings text-5xl md:text-6xl lg:text-7xl tracking-wider text-wild-dark mb-4"
              data-testid="contact-title"
            >
              GET IN TOUCH
            </h1>
            <p className="font-subheadings text-lg md:text-xl text-wild-stone italic max-w-2xl mx-auto">
              Questions? We'd love to hear from you
            </p>
          </div>
        </section>

        {/* Contact Content */}
        <section className="py-16 md:py-24 bg-wild-sand">
          <div className="max-w-4xl mx-auto px-6 md:px-12">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
              {/* Contact Info */}
              <div className="space-y-8">
                <div>
                  <h2 className="font-headings text-2xl tracking-wider text-wild-dark mb-6">
                    CONTACT INFO
                  </h2>
                  
                  {/* Phone */}
                  <a
                    href="tel:8032725881"
                    className="flex items-center gap-4 p-4 bg-white border border-wild-khaki hover:border-wild-leather transition-colors group mb-4"
                    data-testid="contact-phone"
                  >
                    <div className="w-12 h-12 flex items-center justify-center bg-wild-leather/10 group-hover:bg-wild-leather/20 transition-colors">
                      <Phone className="h-6 w-6 text-wild-leather" />
                    </div>
                    <div>
                      <p className="font-headings text-sm tracking-wider text-wild-stone">
                        CALL US
                      </p>
                      <p className="font-body text-lg text-wild-dark">
                        803-272-5881
                      </p>
                    </div>
                  </a>

                  {/* Location */}
                  <div className="flex items-center gap-4 p-4 bg-white border border-wild-khaki">
                    <div className="w-12 h-12 flex items-center justify-center bg-wild-leather/10">
                      <MapPin className="h-6 w-6 text-wild-leather" />
                    </div>
                    <div>
                      <p className="font-headings text-sm tracking-wider text-wild-stone">
                        LOCATION
                      </p>
                      <p className="font-body text-lg text-wild-dark">
                        South Carolina, USA
                      </p>
                    </div>
                  </div>
                </div>

                {/* Social Links */}
                <div>
                  <h2 className="font-headings text-2xl tracking-wider text-wild-dark mb-6">
                    FOLLOW US
                  </h2>
                  <div className="flex gap-4">
                    <a
                      href="https://www.tiktok.com/@dandhayy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 px-6 py-4 bg-white border border-wild-khaki hover:border-wild-leather hover:bg-wild-leather hover:text-white transition-all group"
                      data-testid="contact-tiktok"
                    >
                      <TikTokIcon className="h-6 w-6" />
                      <span className="font-headings tracking-wider">
                        TIKTOK
                      </span>
                    </a>
                    <a
                      href="https://www.youtube.com/@dhaymanzo"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 px-6 py-4 bg-white border border-wild-khaki hover:border-wild-leather hover:bg-wild-leather hover:text-white transition-all group"
                      data-testid="contact-youtube"
                    >
                      <YouTubeIcon className="h-6 w-6" />
                      <span className="font-headings tracking-wider">
                        YOUTUBE
                      </span>
                    </a>
                  </div>
                </div>
              </div>

              {/* Additional Info */}
              <div className="bg-white border border-wild-khaki p-8">
                <h2 className="font-headings text-2xl tracking-wider text-wild-dark mb-6">
                  WILD ONES LLC
                </h2>
                <div className="space-y-6">
                  <p className="font-body text-wild-stone leading-relaxed">
                    We're a small southern clothing brand with a passion for the outdoors. 
                    Whether you're a hunter, fisher, or just love the wild life, we've got 
                    something for you.
                  </p>
                  <p className="font-body text-wild-stone leading-relaxed">
                    Got questions about sizing, shipping, or just want to chat? Give us a call — 
                    we're always happy to help out a fellow Wild One.
                  </p>
                  <div className="pt-4 border-t border-wild-khaki">
                    <p className="font-subheadings text-lg text-wild-leather italic">
                      "Born Wild. Stay Wild."
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
};
